import { IHubTender } from '../hubTender/hubTender';
import { ILocation } from '../global/geoModels';
import {
  IComplaint,
  IFleet,
  IMeetingPoint,
  ITransferCompanies,
  ITransferCompany,
} from '../transferCompany/transferCompany';
import { IVehicle } from '../vehicle/vehicle';
import { IHubMeetingPoint } from '../hub/hub';
import { TravelAddon } from '../travelAddon/travelAddon';
import { IDriver, IDriverTP } from '../driver/driver';
import { IBooking } from '../booking/booking';
import { IBillableJourney } from '../invoice/BillableJourney';
import { IQuote } from '../quote/quote';
import { IUser } from '../user/user';
import { IPagination, IPaginationParams } from '../global/pagination';
import { ISortParams } from '../global/sort';
import { ISearchActionsList } from '../journeyExecution/journeyExecution';

export enum JourneyStatusType {
  NotPaid = 'NOT_PAID',
  AwaitingApproval = 'AWAITING_APPROVAL',
  Pending = 'PENDING',
  Confirmed = 'CONFIRMED',
  Planned = 'PLANNED',
  DriverUnderway = 'DRIVER_UNDERWAY',
  DriverArrived = 'DRIVER_ARRIVED',
  JourneyInProgress = 'JOURNEY_IN_PROGRESS',
  Completed = 'COMPLETED',
  SupplierCancelled = 'SUPPLIER_CANCELLED',
  BookerCancelled = 'BOOKER_CANCELLED',
  CancelledFree = 'CANCELLED_FREE',
  CancelledWithCosts = 'CANCELLED_WITH_COSTS',
  Rejected = 'REJECTED'
}

export const JourneyStatusDisplayName = {
  [JourneyStatusType.NotPaid]: 'Not paid',
  [JourneyStatusType.AwaitingApproval]: 'Awaiting approval',
  [JourneyStatusType.Pending]: 'Pending',
  [JourneyStatusType.Confirmed]: 'Confirmed',
  [JourneyStatusType.Planned]: 'Planned',
  [JourneyStatusType.DriverUnderway]: 'Driver underway',
  [JourneyStatusType.DriverArrived]: 'Driver arrived',
  [JourneyStatusType.JourneyInProgress]: 'Journey in progress',
  [JourneyStatusType.Completed]: 'Completed',
  [JourneyStatusType.SupplierCancelled]: 'Supplier cancelled',
  [JourneyStatusType.BookerCancelled]: 'Booker cancelled',
  [JourneyStatusType.CancelledFree]: 'Cancelled free',
  [JourneyStatusType.CancelledWithCosts]: 'Cancelled with costs',
  [JourneyStatusType.Rejected]: 'Rejected'
};

export enum JourneyCancellationReasonType {
  DriverNoShow = 'DRIVER_NO_SHOW',
  NotNeededAnymore = 'NOT_NEEDED_ANYMORE',
  NoAvailability = 'NO_AVAILABILITY',
  AddressInvalid = 'ADDRESS_INVALID',
  InsufficientTravellerDetails = 'INSUFFICIENT_TRAVELLER_DETAILS',
  TechnicalIssue = 'TECHNICAL_ISSUE',
  TravellerNoShow = 'TRAVELLER_NO_SHOW',
  PriceCalculationError = 'PRICE_CALCULATION_ERROR',
  StrikeOrRoadClosure = 'STRIKE_OR_ROAD_CLOSURE',
  NoFlightNumber = 'NO_FLIGHT_NUMBER',
  AddOnIssue = 'ADD_ON_ISSUE',
  CantFulfillCustomerRequest = 'CANT_FULFILL_CUSTOMER_REQUEST',
  PaxBagsConfiguration = 'PAX_BAGS_CONFIGURATION',
  ForceMajeure = 'FORCE_MAJEURE',
  Other = 'OTHER'
}

export const JourneyCancellationReasonDisplayName = {
  [JourneyCancellationReasonType.NotNeededAnymore]: 'Not need anymore',
  [JourneyCancellationReasonType.DriverNoShow]: 'Driver no show',
  [JourneyCancellationReasonType.Other]: 'Other',
  [JourneyCancellationReasonType.NoAvailability]: 'No availability',
  [JourneyCancellationReasonType.AddressInvalid]: 'Address invalid',
  [JourneyCancellationReasonType.InsufficientTravellerDetails]: 'Insufficient traveller details',
  [JourneyCancellationReasonType.TechnicalIssue]: 'Technical issue',
  [JourneyCancellationReasonType.TravellerNoShow]: 'Traveller no show',
  [JourneyCancellationReasonType.PriceCalculationError]: 'Price calculation error',
  [JourneyCancellationReasonType.StrikeOrRoadClosure]: 'Strike or road closure',
  [JourneyCancellationReasonType.NoFlightNumber]: 'No flight number',
  [JourneyCancellationReasonType.AddOnIssue]: 'Add-on issue',
  [JourneyCancellationReasonType.CantFulfillCustomerRequest]: 'Cant fulfill customer request',
  [JourneyCancellationReasonType.PaxBagsConfiguration]: 'Pax/bags configuration',
  [JourneyCancellationReasonType.ForceMajeure]: 'Force majeure',
};

export enum JourneyCorrectionReasonType {
  DriverNoShow = 'DRIVER_NO_SHOW',
  DriverLateLessThan15Min = 'DRIVER_LATE_LESS_THAN_15_MIN',
  DriverLateMoreThan15Min = 'DRIVER_LATE_MORE_THAN_15_MIN',
  AddonsNotProvided = 'ADDONS_NOT_PROVIDED',
  IncorrectDropOff = 'INCORRECT_DROP_OFF',
  DriverUsingPhone = 'DRIVER_USING_PHONE',
  ReckLessDriver = 'RECKLESS_DRIVING',
  AskForAdditionalPayment = 'ASK_FOR_ADDITIONAL_PAYMENT',
  UnpoliteDriver = 'UNPOLITE_DRIVER',
  DirtyVehicle = 'DIRTY_VEHICLE',
  WrongVehicleType = 'WRONG_VEHICLE_TYPE',
  UncomfortableVehicle = 'UNCOMFORTABLE_VEHICLE',
  MeterVisible = 'METER_VISIBLE',
  Other = 'OTHER',
}

export const JourneyCorrectionReasonTypeDisplayName = {
  [JourneyCorrectionReasonType.DriverNoShow]: 'Driver no-show (150%)',
  [JourneyCorrectionReasonType.DriverLateMoreThan15Min]: 'Pickup: Driver late > 15 min (50%)',
  [JourneyCorrectionReasonType.DriverLateLessThan15Min]: 'Pickup: Driver late < 15 min (0%)',
  [JourneyCorrectionReasonType.AddonsNotProvided]: 'Journey: Addons not provided (25%)',
  [JourneyCorrectionReasonType.IncorrectDropOff]: 'Journey: Incorrect drop off (0%)',
  [JourneyCorrectionReasonType.DriverUsingPhone]: 'Driver: Using phone (25%)',
  [JourneyCorrectionReasonType.ReckLessDriver]: 'Driver: Reckless driving (25%)',
  [JourneyCorrectionReasonType.AskForAdditionalPayment]: 'Driver: Asked for additional payment (25%)',
  [JourneyCorrectionReasonType.UnpoliteDriver]: 'Driver: Unpolite (25%)',
  [JourneyCorrectionReasonType.DirtyVehicle]: 'Vehicle: Dirty vehicle (25%)',
  [JourneyCorrectionReasonType.WrongVehicleType]: 'Vehicle: Wrong vehicle type (0%)',
  [JourneyCorrectionReasonType.UncomfortableVehicle]: 'Vehicle: Uncomfortable vehicle (25%)',
  [JourneyCorrectionReasonType.MeterVisible]: 'Vehicle: Meter visible (25%)',
  [JourneyCorrectionReasonType.Other]: 'Other',
};

export interface ICancellationDetails {
  cancellable: boolean;
  freeCancellationUntil: string;
  freeCancellationWindowHours: number;
}

export enum DeclineJourneyOfferReasonType {
  FareTooLow = 'FARE_TOO_LOW',
  NoVehicleAvailability = 'NO_VEHICLE_AVAILABILITY',
  NoDriverAvailability = 'NO_DRIVER_AVAILABILITY',
  AddonsNotAvailable = 'ADDONS_NOT_AVAILABLE',
  IncorrectJourneyDetails = 'INCORRECT_JOURNEY_DETAILS',
  Holiday = 'HOLIDAY',
  Strike = 'STRIKE',
  DistanceTooShortOrLong = 'DISTANCE_TOO_SHORT_OR_LONG',
  Other = 'OTHER'
}

export const DeclineJourneyOfferReasonDisplayName = {
  [DeclineJourneyOfferReasonType.FareTooLow]: 'Fare too low',
  [DeclineJourneyOfferReasonType.NoVehicleAvailability]: 'No vehicle availability',
  [DeclineJourneyOfferReasonType.NoDriverAvailability]: 'No driver availability',
  [DeclineJourneyOfferReasonType.AddonsNotAvailable]: 'Addons not available',
  [DeclineJourneyOfferReasonType.IncorrectJourneyDetails]: 'Incorrect journey details',
  [DeclineJourneyOfferReasonType.Holiday]: 'Holiday',
  [DeclineJourneyOfferReasonType.Strike]: 'Strike',
  [DeclineJourneyOfferReasonType.DistanceTooShortOrLong]: 'Distance too short or long',
  [DeclineJourneyOfferReasonType.Other]: 'Other'
}

export enum DeclineJourneyOfferMethodType {
  Email = 'EMAIL',
  Portal = 'PORTAL',
  Api = 'API',
  Unknown = 'UNKNOWN'
}

export interface IDeclineJourneyOfferByTokenParams {
  token: string;
  method: DeclineJourneyOfferMethodType;
  reason: DeclineJourneyOfferReasonType;
  description?: string;
}

export interface IDeclineJourneyOfferParams {
  id: number;
  method: DeclineJourneyOfferMethodType;
  reason: DeclineJourneyOfferReasonType;
  description?: string;
}

export interface IJourney {
  id: number;
  totalFare: number;
  totalPrice: number;
  hubId: number;
  created: string;
  updated: string;
  bookingId: number;
  journeyCode: string;
  canBeCompleted: boolean;
  quoteId: number;
  originLocationId: number;
  originLocation: ILocation;
  destinationLocationId: number;
  destinationLocation: ILocation;
  status: string;
  heldForComplaintId: number;
  heldForComplaint: IComplaint;
  travellerInfo: ITravellerInfo;
  journeyExecutionInfo: IJourneyExecutionInfo;
  priceCalculationInfo: IPriceCalculationInfo;
  cancellationDetails?: ICancellationDetails;
  meetingPoint?: IHubMeetingPoint | IMeetingPoint;
  transferCompanies?: ITransferCompanies;
  fleet?: IFleet;
  vehicle?: IVehicle;
  travelAddons: TravelAddon[];
  driver?: IDriver;
  hubTender?: IHubTender;
  nextAction?: string;
  booking: IBooking;
  inbound?: boolean;
  completedOn: string;
  billableJourney: IBillableJourney;
  quote: IQuote;
  hubMeetingPoints: [IHubMeetingPoint];
  transferCompany?: ITransferCompany;
  travellerPrice: number;
  partnerCommissionPrice: number;
  fareIncludingVat: number;
  partnerPriceIncludingVat: number;
  priceSummary: IPriceSummary;
  searchActionsTP: ISearchActionsList;
  driverTP: IDriverTP;
  vehicleTP: IVehicleTP;
}

export interface IVehicleTP {
  makeName: string;
  modelName: string;
  licensePlate: string;
  seriesName: string;
}

export interface IPriceSummary {
  commissionIncludingVat: number;
  commissionExcludingVat: number;
  commissionVat: number;
  priceIncludingVat: number;
  priceExcludingVat: number;
  priceVat: number;
  partnerPriceIncludingVat: number;
  partnerPriceExcludingVat: number;
  partnerPriceVat: number;
  fareIncludingVat: number;
  fareExcludingVat: number;
  fareVat: number;
  fareCurrency: string;
}

export interface IJourneysPerCountry {
  countryCode: string;
  count: number
}

export interface IJourneysPerCountryRequestParams extends IPaginationParams {
  createdBefore: string;
  createdAfter: string;
  excludedStatuses: string[];
  partnersIds: number[];
}

export interface ITravellerInfo {
  id?: number;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  meetAndGreetSignText?: string;
  passengerCount?: number;
  adultPassengerCount?: number;
  infantPassengerCount?: number;
  childPassengerCount?: number;
  luggageCount: number;
  driverComments?: string;
  flightNumber?: string;
  trainNumber?: string;
  languageIsoCode?: string;
  residenceCountryCode?: string;
  phoneVerificationInfo?: IPhoneVerificationInfo;
  allowedToModifyJourney?: boolean;
  displayEmailAllowed?: boolean;
}

export interface IPhoneVerificationInfo {
  verifiedAt: string;
  valid: boolean;
  lineType: string
  formattedPhone: string;
  countryPrefix: string;
}

export interface IJourneyExecutionInfo {
  vehicleCategory: string;
  pickupDate: string;
  pickupTimeZone: string;
  fleetId: number;
  driverId: number;
  vehicleId: number;
  cancellationReason: string;
  transferCompanyId: number;
  meetingPointId: number;
  meetingPointType: string;
  distance: IDistance;
  executionCode: string;
  driverCode: string;
  driverLink: string;
}

export interface IDistance {
  unit: string;
  value: number;
}

export interface IPriceCalculationInfo {
  currencyCode: string;
  price: number;
  priceVat: number;
  fare: number;
  fareVatLevel: number;
  fixedPrice: number;
  partnerCommission: number;
  fareCurrencyCode: string;
  platformMarkup: number;
  platformMarkupCurrencyCode: string;
  platformFee: number;
  partnerBusinessModel: string;
}

export interface IJourneyPickup {
  dateTime: string;
  timeZoneId: string;
}

export enum JourneyNextActions {
  Approve = 'APPROVE',
  AcceptJourney = 'ACCEPT_JOURNEY',
  PlanJourney = 'PLAN_JOURNEY',
  CompleteJourney = 'COMPLETE_JOURNEY',
  PerformJourney = 'PERFORM_JOURNEY',
  None = 'NONE',
}

export const JourneyNextActionsDisplayName = {
  [JourneyNextActions.Approve]: 'Approve',
  [JourneyNextActions.AcceptJourney]: 'Accept journey',
  [JourneyNextActions.PlanJourney]: 'Select driver or vehicle',
  [JourneyNextActions.CompleteJourney]: 'Complete journey',
  [JourneyNextActions.PerformJourney]: 'Perform journey',
  [JourneyNextActions.None]: 'None',
};

export interface IJourneyRequestParams {
  id: number;
  includeHistory?: boolean;
  includeCancellationDetails?: boolean;
}

export interface IJourneysRequestParams extends IPaginationParams, ISortParams {
  journeyId?: string;
  bookingId?: number;
  bookingCode?: string;
  journeyCode?: string;
  includedStatuses?: string[];
  excludedStatuses?: string[];
  pickupDateAfter?: string;
  pickupDateBefore?: string;
  updatedBefore?: string;
  updatedAfter?: string;
  assignedTransferCompanyId?: number;
  assignedFleetId?: number;
  nextJourneyAction?: string;
  completedOnAfter?: string;
  completedOnBefore?: string;
  hubName?: string;
  bookerReference?: string;
  travellerEmailAddress?: string;
  travellerName?: string;
  destinationManagerId?: number;
  partnersIds?: number[];
  createdBefore?: string;
  createdAfter?: string;
}

export interface IGenerateJourneyExcelReportRequestParams extends ISortParams {
  journeyId?: string;
  bookingId?: number;
  bookingCode?: string;
  journeyCode?: string;
  includedStatuses?: string[];
  excludedStatuses?: string[];
  pickupDateAfter?: string;
  pickupDateBefore?: string;
  updatedBefore?: string;
  updatedAfter?: string;
  assignedTransferCompanyId?: number;
  assignedFleetId?: number;
  nextJourneyAction?: string;
  completedOnAfter?: string;
  completedOnBefore?: string;
  hubName?: string;
  bookerReference?: string;
  travellerEmailAddress?: string;
  travellerName?: string;
  destinationManagerId?: number;
  partnersIds?: number[];
  createdBefore?: string;
  createdAfter?: string;
}

export interface IJourneyExcelReport {
  id: number,
  status: string
}

export interface IExcelReport {
  id: number,
  externalKey: string,
  contentType: string
}

export interface IJourneyCancellationParams {
  id: number;
  reason: string;
  acceptCosts?: boolean;
  overrideCancellationWindow?: boolean;
}

export interface IJourneyChangesRequestParams {
  journeyId: number;
  page?: string;
  size?: string;
}

export interface IJourneyChange {
  id: number;
  created: string;
  updated: string;
  journeyId: number;
  changedByUserId: number;
  changedAt: string;
  journeyChangeType: string;
  version: number;
  comment: string;
  user: IUser;
  valueChanges: [IJourneyValueChange];
  changeAutomationType: string;
}

export interface IJourneyValueChange {
  fieldName: string;
  oldValue: string;
  newValue: string;
}

export interface IJourneyChangesInfo {
  afterMinBookingWindow: boolean;
}

export enum JourneyChangeAutomationType {
  AutomationNotSupported = 'AUTOMATION_NOT_SUPPORTED',
  PaymentAndReOrchestration = 'PAYMENT_AND_RE_ORCHESTRATION',
  InformTransco = 'INFORM_TRANSCO',
  ReOrchestration = 'RE_ORCHESTRATION',
}

export enum JourneyHistoryStatusType {
  Confirmed = 'CONFIRMED',
  WaitingForPayment = 'WAITING_FOR_PAYMENT',
  Informed = 'INFORMED',
  Offered = 'OFFERED',
  Paid = 'PAID',
}

export const JourneyHistoryStatusDisplayName = {
  [JourneyHistoryStatusType.Confirmed]: 'Confirmed',
  [JourneyHistoryStatusType.WaitingForPayment]: 'Waiting for payment',
  [JourneyHistoryStatusType.Informed]: 'Informed',
  [JourneyHistoryStatusType.Offered]: 'Offered',
  [JourneyHistoryStatusType.Paid]: 'Paid',
};

export interface IPendingJourney {
  journeyId: number;
  hoursSinceCreation: number;
  hoursUntilPickup: number;
  priority: string;
  journey?: IJourney;
}

export interface IPendingJourneyRequestParams extends IPaginationParams {
  includePriorities?: [];
  excludePriorities?: [];
}

interface IHubBriefInfo {
  hubId: number;
  hubName: string;
  iataCode: string;
}

export interface IJourneyPerHub {
  hubBriefInfo: IHubBriefInfo;
  count: number;
}

export interface IJourneyPickup {
  pickupDate: string;
  pickupTimeZone: string;
}

export interface IJourneyLocation {
  locationId: number;
}

export interface IJourneyParams {
  journeyId: number;
  travellerInfo: ITravellerInfo;
  pickup: IJourneyPickup;
  originLocation: IJourneyLocation;
  destinationLocation: IJourneyLocation;
  ignoreMinimumNotice?: boolean;
}

export enum MeetingPointType {
  Hub = 'HUB',
  TransferCompany = 'TRANSFER_COMPANY',
}

export const MeetingPointDisplayName = {
  [MeetingPointType.Hub]: 'Hub',
  [MeetingPointType.TransferCompany]: 'TransferCompany',
};

export enum JourneyExcelReportGenerationStatusType {
  InProgress = 'IN_PROGRESS',
  Failed = 'FAILED',
  Completed = 'COMPLETED',
}

export interface IJourneysHPRequestParams extends IJourneyRequestParams {
  lastName?: string;
  bookingCode?: string; 
}

export interface IJourneyHPRequestParams extends IJourneyRequestParams {
  lastName: string;
  bookingCode: string;
  id: number;
}

export enum DashboardTabType {
  Today = 'TODAY',
  Tomorrow = 'TOMORROW',
}

export interface ITab {
  type: number;
  headerElement: any;
  title: string;
}


export interface IJourneyCorrectionCreateParams {
  journeyId: number;
  reason: string;
  description?: string;
  penaltyPercentage: number;
}

export interface IJourneyCorrectionUpdateParams {
  reason: string;
  description?: string;
  penaltyPercentage: number;
}

export interface IJourneyCorrection {
  id: number;
  journeyId: number;
  created: string;
  reason: string;
  description: string;
  currencyCode: string;
  partnerInvoicingStatus: string;
  amount: number;
  transferCompanyInvoicingStatus: string;
}

export interface IJourneyCorrectionsRequestParams extends IPaginationParams {
  journeyId: number;
}
